// Builds the markdown + spec for the dynamic resolving canvas tab from a HIL
// item. Shared by InboxView (entering a session) and ResolvingView (keeping the
// tab in sync as the active item advances), so the two paths can never drift.

import type { InboxArtifact, InboxItem, ResolvingTabSpec } from '../types';

/** Escape characters that would break a GFM table cell or row. */
function escapeCell(value: string): string {
  return value.replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');
}

/** Render a structured InboxArtifact table as GFM markdown for the canvas tab. */
export function buildArtifactMarkdown(artifact: InboxArtifact): string {
  if (artifact.type !== 'table' || !artifact.columns?.length || !artifact.rows?.length) {
    return '';
  }
  const title = artifact.title?.trim() || 'Related evidence';
  const header = `| ${artifact.columns.map(escapeCell).join(' | ')} |`;
  const divider = `| ${artifact.columns.map(() => '---').join(' | ')} |`;
  const body = artifact.rows.map(
    row => `| ${row.map(cell => escapeCell(String(cell ?? ''))).join(' | ')} |`,
  );
  return ['', `### ${escapeCell(title)}`, '', header, divider, ...body, ''].join('\n');
}

/** Build a minimal markdown summary of a HIL item for the canvas tab panel. */
export function buildHilMarkdown(item: InboxItem): string {
  const parts: string[] = [
    `## ${escapeCell(item.name)}`,
    '',
    `| | |`,
    `|---|---|`,
    `| **Module** | \`${escapeCell(item.moduleId)}\` |`,
    `| **Solution** | \`${escapeCell(item.solutionId)}\` |`,
    `| **Priority** | ${escapeCell(item.priority)} |`,
  ];
  if (item.linkedItemTitle) {
    parts.push(`| **Linked to** | ${escapeCell(item.linkedItemTitle)} |`);
  }
  if (item.attention?.summary) {
    parts.push('', '### Why this needs you', '', item.attention.summary);
  }
  if (item.attention?.objective) {
    parts.push('', '### Objective', '', item.attention.objective);
  }
  if (item.artifact) {
    parts.push(buildArtifactMarkdown(item.artifact));
  }
  return parts.join('\n');
}

/**
 * Prefer agent `ui_presentation`, then append structured evidence so the wide
 * Resolving canvas shows the related-evidence table (not only the ESA rail).
 */
export function buildResolvingMarkdown(item: InboxItem): string {
  const base = (item.uiPresentation ?? '').trim() || buildHilMarkdown(item);
  if (!item.artifact || item.artifact.type !== 'table') {
    return base;
  }
  // ui_presentation already covers findings as bullets; still append the table
  // so the spacious canvas shows Check / Expected / Found / Status.
  if (!(item.uiPresentation ?? '').trim()) {
    return base; // buildHilMarkdown already included artifact
  }
  const tableMd = buildArtifactMarkdown(item.artifact).trim();
  if (!tableMd) {
    return base;
  }
  return `${base}\n\n${tableMd}`;
}

/** Build the full dynamic-tab spec for a HIL item. */
export function buildResolvingTabSpec(item: InboxItem): ResolvingTabSpec {
  return {
    title: item.name,
    hilId: item.id,
    moduleId: item.moduleId,
    solutionId: item.solutionId,
    markdownContent: buildResolvingMarkdown(item),
  };
}
